<template>
  <div>
    <h2 class="mc-h2">Check and submit</h2>
    <p class="mc-lede">
      Have a quick look through. Tap <b>Edit</b> on anything that needs
      changing.
    </p>

    <review-group-card
      v-for="group in groups"
      :key="group.key"
      :title="group.title"
      :rows="group.rows"
      edit-label="Edit"
      :editing="openKey === group.key"
      @edit="onEdit(group.key)"
    >
      <slot v-if="openKey === group.key" name="editor" :group-key="group.key" />
    </review-group-card>

    <template v-if="requireSignature">
      <h3 class="mc-h3">Sign your claim — type your full name</h3>
      <div class="mc-field">
        <label class="mc-sr" for="mc-signature">Your full name</label>
        <input
          id="mc-signature"
          type="text"
          placeholder="Your full name"
          class="mc-signature-input"
          :value="signature"
          @input="$emit('update-signature', $event.target.value)"
        />
        <p class="mc-hint">
          Typing your name here has the same effect as signing the form.
        </p>
      </div>
    </template>

    <div class="mc-card mc-card--flat">
      <div
        class="mc-chkrow"
        :class="{ 'is-on': consent }"
        @click="$emit('update-consent', !consent)"
      >
        <div class="mc-box">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            stroke-width="3.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <div class="mc-cl">
          I confirm the information above is true and complete to the best of my
          knowledge, and I consent to APA verifying it.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReviewGroupCard from './ReviewGroupCard.vue'

// Shared between the online-form and (later) AI-voice journeys — the design's
// own ROUTES.f7 feeds either journey's groups into this exact same
// review+sign screen. Document capture is now its own step (DocumentsStep).
export default {
  name: 'ReviewSignStep',
  components: { ReviewGroupCard },
  props: {
    // [{ key, title, rows: [[label, value], ...] }]
    groups: { type: Array, required: true },
    signature: { type: String, default: '' },
    consent: { type: Boolean, default: false },
    // the paper-upload journey already carries a signed physical form, so it
    // opts out of collecting a second, typed signature here
    requireSignature: { type: Boolean, default: true },
    // edit in the card via the `editor` slot instead of emitting edit-group
    inlineEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      // group open for editing; only one at a time
      openKey: null,
    }
  },
  methods: {
    onEdit(key) {
      if (!this.inlineEdit) {
        this.$emit('edit-group', key)
        return
      }
      this.openKey = this.openKey === key ? null : key
      // lets the parent re-gate submission on what was edited
      if (this.openKey) this.$emit('edit-opened', key)
    },
  },
}
</script>

<style scoped>
.mc-h2 {
  font-size: 19px;
  line-height: 1.3;
  font-weight: 700;
  margin: 0 0 6px;
  color: #14142b;
}

.mc-h3 {
  font-size: 13px;
  font-weight: 600;
  margin: 22px 0 10px;
  color: #3d4450;
}

.mc-lede {
  font-size: 14.5px;
  color: #6e7191;
  margin: 0 0 20px;
}

.mc-hint {
  font-size: 12.5px;
  color: #6e7191;
  margin: 6px 0 0;
}

.mc-field {
  margin: 0 0 16px;
}

/* visually hidden but still readable by screen readers — the heading above
   already does the visible labelling job */
.mc-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.mc-signature-input {
  width: 100%;
  font-family: 'Brush Script MT', cursive, inherit;
  font-size: 19px;
  letter-spacing: 0.5px;
  color: #333333;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 13px 14px;
}

.mc-signature-input:focus {
  outline: 0;
  border-color: #0066cc;
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.13);
}

.mc-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
}

.mc-card--flat {
  box-shadow: none;
}

.mc-chkrow {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  cursor: pointer;
}

.mc-box {
  width: 21px;
  height: 21px;
  flex: 0 0 21px;
  border-radius: 6px;
  border: 2px solid #a0a3bd;
  display: grid;
  place-items: center;
  margin-top: 1px;
  transition: 0.15s;
}

.mc-box svg {
  opacity: 0;
}

.mc-chkrow.is-on .mc-box {
  background: #0066cc;
  border-color: #0066cc;
}

.mc-chkrow.is-on .mc-box svg {
  opacity: 1;
}

.mc-cl {
  font-size: 13.5px;
  color: #333333;
  font-weight: 500;
}
</style>
